/* ============================================================================
 * useWallet — one hook for the whole identity surface
 * ----------------------------------------------------------------------------
 * This is the ONLY place the OS reads account state. Every component — the
 * DIGITAL_EMBASSY.EXE window, the taskbar chip, the desktop wallpaper readout —
 * consumes this, so wallet behaviour (including the no-auto-popup rule) is
 * enforced once.
 *
 * NO AUTO-POPUP: nothing here signs or connects on mount. `connect()` is only
 * ever called from an explicit user click. ENS lookups are read-only calls and
 * safe to run on load.
 * ========================================================================= */

import { useCallback, useMemo } from 'react';
import {
  useAccount,
  useConnect,
  useDisconnect,
  useEnsAvatar,
  useEnsName,
  useSwitchChain,
  useWalletClient,
} from 'wagmi';
import { mainnet } from 'wagmi/chains';
import { DEFAULT_CHAIN_ID } from '../lib/wagmi';
import { ORG_ENS_NAME } from '../lib/supercompute';

export interface WalletSession {
  address: `0x${string}` | undefined;
  isConnected: boolean;
  isConnecting: boolean;
  isReconnecting: boolean;
  chainId: number | undefined;
  onDefaultChain: boolean;
  /** Forward-resolved ENS name for the connected address (mainnet). */
  ensName: string | undefined;
  ensNameLoading: boolean;
  /** ENS avatar URI for the connected address. */
  ensAvatar: string | undefined;
  /** Display handle: ENS name → short address → "GUEST". */
  handle: string;
  shortAddress: string | undefined;
  connect: () => void;
  disconnect: () => void;
  switchToDefaultChain: () => void;
  error: string | undefined;
  availableConnectors: { id: string; name: string; type: string }[];
  orgEnsName: string;
  walletClient: ReturnType<typeof useWalletClient>['data'];
}

export function shortAddr(a?: string): string | undefined {
  return a ? `${a.slice(0, 6)}…${a.slice(-4)}` : undefined;
}

export function useWallet(): WalletSession {
  const { address, isConnected, status, chainId } = useAccount();
  const { connect, connectors, isPending, error } = useConnect();
  const { disconnect } = useDisconnect();
  const { switchChain } = useSwitchChain();
  const { data: walletClient } = useWalletClient();
  const { data: ensName, isLoading: ensNameLoading } = useEnsName({
    address,
    chainId: mainnet.id,
  });
  const { data: ensAvatar } = useEnsAvatar({
    name: ensName ?? undefined,
    chainId: mainnet.id,
  });

  const primary = connectors[0];
  const doConnect = useCallback(() => {
    if (primary) connect({ connector: primary });
  }, [connect, primary]);

  const switchToDefaultChain = useCallback(() => {
    switchChain({ chainId: DEFAULT_CHAIN_ID });
  }, [switchChain]);

  const availableConnectors = useMemo(
    () =>
      connectors.map((c) => ({ id: c.id, name: c.name, type: c.type as string })),
    [connectors],
  );

  const short = shortAddr(address);

  return {
    address,
    isConnected,
    isConnecting: isPending,
    isReconnecting: status === 'reconnecting',
    chainId,
    onDefaultChain: chainId === DEFAULT_CHAIN_ID,
    ensName: ensName ?? undefined,
    ensNameLoading,
    ensAvatar: ensAvatar ?? undefined,
    handle: ensName ?? short ?? 'GUEST',
    shortAddress: short,
    connect: doConnect,
    disconnect,
    switchToDefaultChain,
    error: error?.message,
    availableConnectors,
    orgEnsName: ORG_ENS_NAME,
    walletClient,
  };
}
